import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from "recharts";
import { AudioWaveform, AlertTriangle, CheckCircle, AlertCircle, XCircle, Info } from "lucide-react";

const statusConfig = {
  critical: { color: "#EF4444", bg: "bg-red-50", border: "border-red-200", text: "text-red-700", icon: XCircle, label: "CRITICAL" },
  elevated: { color: "#F59E0B", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: AlertTriangle, label: "ELEVATED" },
  normal: { color: "#10B981", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: CheckCircle, label: "NORMAL" },
  low: { color: "#0EA5E9", bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", icon: Info, label: "LOW" },
  not_detected: { color: "#CBD5E1", bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-400", icon: Info, label: "N/A" },
};

const barColor = (status) => {
  const map = { critical: "#EF4444", elevated: "#F59E0B", normal: "#10B981", low: "#0EA5E9", not_detected: "#E2E8F0" };
  return map[status] || "#94A3B8";
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-3 min-w-[200px]">
      <p className="text-sm font-bold text-slate-800 mb-1">{d.name}</p>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Measured:</span><span className="font-mono font-bold">{d.measured_db} dB</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Ideal:</span><span className="font-mono">{d.ideal_db} dB</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Tolerance:</span><span className="font-mono">&plusmn;{d.tolerance} dB</span></div>
        <div className="flex justify-between">
          <span className="text-slate-500">Deviation:</span>
          <span className={`font-mono font-bold ${d.deviation > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
            {d.deviation > 0 ? '+' : ''}{d.deviation} dB
          </span>
        </div>
        <div className="flex justify-between"><span className="text-slate-500">Status:</span><span className="font-bold uppercase">{d.status}</span></div>
      </div>
      <p className="text-sm text-slate-400 mt-2">{d.freq_range}</p>
    </div>
  );
};

function SoundBar({ sound }) {
  const config = statusConfig[sound.status] || statusConfig.not_detected;
  const Icon = config.icon;
  const maxDb = Math.max(sound.measured_db, sound.ideal_db, 60);
  const measuredPct = Math.min(100, (sound.measured_db / maxDb) * 100);
  const idealPct = Math.min(100, (sound.ideal_db / maxDb) * 100);

  if (sound.status === "not_detected") return null;

  return (
    <div className={`p-3 rounded-sm border ${config.border} ${config.bg} transition-all duration-200`} data-testid={`sound-type-${sound.name.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={14} className={config.text} />
          <span className="text-sm font-bold text-slate-800">{sound.name}</span>
        </div>
        <Badge className={`rounded-full text-sm font-bold uppercase tracking-widest border ${config.bg} ${config.text} ${config.border}`}>
          {config.label}
        </Badge>
      </div>

      {/* dB bar comparison */}
      <div className="space-y-1.5 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400 w-16 uppercase tracking-wider font-bold">Measured</span>
          <div className="flex-1 h-3 bg-white rounded-full overflow-hidden border border-slate-200">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${measuredPct}%`, backgroundColor: barColor(sound.status) }}
            />
          </div>
          <span className="text-sm font-mono font-bold w-14 text-right" style={{ color: barColor(sound.status) }}>
            {sound.measured_db} dB
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400 w-16 uppercase tracking-wider font-bold">Ideal</span>
          <div className="flex-1 h-3 bg-white rounded-full overflow-hidden border border-slate-200">
            <div
              className="h-full bg-slate-300 rounded-full"
              style={{ width: `${idealPct}%` }}
            />
          </div>
          <span className="text-sm font-mono text-slate-400 w-14 text-right">{sound.ideal_db} dB</span>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-400">{sound.freq_range}</span>
        <span className={`font-mono font-bold ${sound.deviation > 0 ? 'text-red-600' : sound.deviation < -5 ? 'text-sky-600' : 'text-emerald-600'}`}>
          {sound.deviation > 0 ? '+' : ''}{sound.deviation} dB deviation
        </span>
      </div>

      <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{sound.description}</p>

      {sound.status === "critical" || sound.status === "elevated" ? (
        <p className="text-sm text-slate-400 mt-1">
          <span className="font-bold">Possible cause:</span> {sound.abnormal_cause}
        </p>
      ) : null}

      {sound.transient_rate !== undefined && (
        <p className="text-sm text-slate-400 mt-0.5">
          <span className="font-bold">Transients:</span> {sound.transient_label} ({sound.transient_rate}/sec)
        </p>
      )}
      {sound.character && (
        <p className="text-sm text-slate-400 mt-0.5">
          <span className="font-bold">Character:</span> {sound.character}
        </p>
      )}
    </div>
  );
}

export default function SoundTypeAnalysis({ data }) {
  if (!data || !data.sound_types) return null;

  const summary = data.summary || {};
  const detected = data.sound_types.filter(s => s.status !== "not_detected");
  
  // Sort by measured_db descending (highest first) for consistent display
  const sortedDetected = [...detected].sort((a, b) => b.measured_db - a.measured_db);
  
  const chartData = sortedDetected.map(s => ({
    name: s.name.length > 12 ? s.name.substring(0, 12) + '..' : s.name,
    fullName: s.name,
    measured_db: s.measured_db,
    ideal_db: s.ideal_db,
    tolerance: s.tolerance,
    deviation: s.deviation,
    status: s.status,
    freq_range: s.freq_range,
    description: s.description,
  }));
  
  // Reverse for chart display (so highest appears at top)
  const chartDataReversed = [...chartData].reverse();

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="sound-type-analysis-card">
      <div className="border-b border-slate-200 p-4 bg-slate-50/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AudioWaveform size={18} className="text-slate-500" />
          <span className="text-sm font-bold uppercase tracking-wider text-slate-500">Engine Sound Type Analysis</span>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="rounded-full text-sm font-bold tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-200 px-3">
            {summary.normal_count || 0} Normal
          </Badge>
          {summary.elevated_count > 0 && (
            <Badge className="rounded-full text-sm font-bold tracking-widest bg-amber-100 text-amber-800 border border-amber-200 px-3">
              {summary.elevated_count} Elevated
            </Badge>
          )}
          {summary.critical_count > 0 && (
            <Badge className="rounded-full text-sm font-bold tracking-widest bg-red-100 text-red-800 border border-red-200 px-3">
              {summary.critical_count} Critical
            </Badge>
          )}
        </div>
      </div>
      <CardContent className="p-5">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
          <div className="p-4 bg-slate-50 rounded-sm border border-slate-100 text-center">
            <p className="text-sm font-bold uppercase tracking-widest text-slate-400">Sound Health</p>
            <p className="text-3xl font-bold font-['Barlow'] text-slate-900" data-testid="sound-health-score">{summary.sound_health_score || 0}</p>
            <p className="text-sm text-slate-400">/100</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-sm border border-slate-100 text-center">
            <p className="text-sm font-bold uppercase tracking-widest text-slate-400">Detected</p>
            <p className="text-3xl font-bold font-['Barlow'] text-slate-900">{summary.detected_count || 0}</p>
            <p className="text-sm text-slate-400">/{summary.total_types_analyzed || 16} types</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-sm border border-slate-100 text-center">
            <p className="text-sm font-bold uppercase tracking-widest text-slate-400">Overall Level</p>
            <p className="text-3xl font-bold font-['Barlow'] text-slate-900">{summary.overall_db || 0}</p>
            <p className="text-sm text-slate-400">dB</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-sm border border-slate-100 text-center">
            <p className="text-sm font-bold uppercase tracking-widest text-slate-400">Alerts</p>
            <p className={`text-3xl font-bold font-['Barlow'] ${(summary.critical_count || 0) > 0 ? 'text-red-600' : (summary.elevated_count || 0) > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {(summary.critical_count || 0) + (summary.elevated_count || 0)}
            </p>
            <p className="text-sm text-slate-400">issues</p>
          </div>
        </div>

        <Tabs defaultValue="chart" className="w-full">
          <TabsList className="bg-slate-100 rounded-sm h-10 mb-4">
            <TabsTrigger value="chart" className="text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white px-4" data-testid="tab-sound-chart">
              Sounds Comparison
            </TabsTrigger>
            <TabsTrigger value="details" className="text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white px-4" data-testid="tab-sound-details">
              Sound Wide Analysis
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chart">
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartDataReversed} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#94A3B8' }} domain={[0, 'auto']} tickFormatter={v => `${v}dB`} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#475569' }} width={100} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="ideal_db" fill="#E2E8F0" radius={[0, 2, 2, 0]} name="Ideal" barSize={10} />
                <Bar dataKey="measured_db" radius={[0, 2, 2, 0]} name="Measured" barSize={14}>
                  {chartDataReversed.map((entry, idx) => (
                    <Cell key={idx} fill={barColor(entry.status)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-4 mt-2 text-sm">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-slate-200 rounded-sm inline-block"></span> Ideal Level</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded-sm inline-block"></span> Normal</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 rounded-sm inline-block"></span> Elevated</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded-sm inline-block"></span> Critical</span>
            </div>
          </TabsContent>

          <TabsContent value="details">
            <div className="space-y-2">
              {sortedDetected.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No sound types detected above threshold</p>
              ) : (
                sortedDetected.map((sound, i) => <SoundBar key={i} sound={sound} />)
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
